import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { DemoLanding, useDemo } from './demo';
import { api, ApiError, basePath, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, AuthCard } from '../layout';
import { oauthMessage, SignInWith } from './sign-in-with';

function formValues(event: FormEvent<HTMLFormElement>): Record<string, FormDataEntryValue> {
  event.preventDefault();
  return Object.fromEntries(new FormData(event.currentTarget));
}

export function LoginPage({ notice, oauth }: { notice?: string; oauth?: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const demo = useDemo();
  const login = useMutation({
    mutationFn: (v: Record<string, FormDataEntryValue>) =>
      api.post<Me>('api/v1/auth/login', { email: v.email, password: v.password, remember: v.remember === '1' }),
    onSuccess: async (me) => {
      queryClient.clear();
      await navigate({ to: me.user.mustChangePassword ? '/password' : '/' });
    },
  });
  const [remember, setRemember] = useState(false);
  const back = oauthMessage(t, oauth);
  const form = (
    <>
      {notice ? <Alert kind="success">{t(notice)}</Alert> : null}
      {back ? <Alert kind="error">{back}</Alert> : null}
      {login.isError ? <Alert kind="error">{errorText(t, login.error)}</Alert> : null}
      <SignInWith remember={remember} />
      <form onSubmit={(e) => login.mutate(formValues(e))} autoComplete="on">
        <label>{t('El. paštas')}<input type="email" name="email" autoComplete="username" required autoFocus={!demo.data} /></label>
        <label>{t('Slaptažodis')}<input type="password" name="password" autoComplete="current-password" required /></label>
        <label className="remember-device"><input type="checkbox" name="remember" value="1" checked={remember} onChange={(e) => setRemember(e.target.checked)} /><span>{t('Šiame įrenginyje likti prisijungus 15 dienų')}</span></label>
        <button className="button button--primary" type="submit" disabled={login.isPending}>{t('Prisijungti')}</button>
      </form>
    </>
  );
  // The demo server: a whole page of companies to try, the usual form folded below.
  if (demo.data) return <DemoLanding demo={demo.data} login={form} />;
  return (
    <AuthCard eyebrow={t('Klientų erdvė')} title={t('Prisijungimas')} intro={t('Įveskite savo prieigos duomenis.')}
      footer={<><Link to="/forgot">{t('Pamiršote slaptažodį?')}</Link>{/* A connected panel (served under a website's folder) offers no registration. */}{basePath ? null : <><br />{t('Neturite paskyros?')} <Link to="/register">{t('Registruotis')}</Link></>}</>}>
      {form}
    </AuthCard>
  );
}

/** A registration started with Apple / Google: the signed ticket (and Apple's name) the API put in the fragment. */
function signupFromFragment(): { ticket: string; email: string | null; provider: string; name: string } | null {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const ticket = hash.get('signup');
  if (!ticket) return null;
  try {
    const data = JSON.parse(atob(ticket.split('.')[0]!.replace(/-/g, '+').replace(/_/g, '/'))) as { email: string | null; provider: string; name: string | null };
    return { ticket, email: data.email, provider: data.provider === 'apple' ? 'Apple' : 'Google', name: hash.get('name') ?? data.name ?? '' };
  } catch { return null; }
}

export function RegisterPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [mismatch, setMismatch] = useState(false);
  const [signup] = useState(signupFromFragment);
  // The website's "Individuali veikla" link opens the form for a person working on their own.
  const [kind, setKind] = useState<'company' | 'iv' | 'join'>(() => (new URLSearchParams(window.location.search).get('kind') === 'iv' ? 'iv' : 'company'));
  // Invited (Apple / Google verified the invited e-mail): the invitation first, the other ways on request.
  const invites = useQuery({
    queryKey: ['signup-invites', signup?.ticket], enabled: Boolean(signup), retry: false,
    queryFn: () => api.post<{ invites: { id: number; company: string; role: 'owner' | 'member' | 'viewer'; inviter: string | null }[] }>('api/v1/auth/signup/invites', { signup: signup!.ticket }).then((r) => r.invites),
  });
  const [others, setOthers] = useState(false);
  const accept = useMutation({
    mutationFn: (v: { invite: number; name: string }) => api.post('api/v1/auth/register', { signup: signup!.ticket, invite: v.invite, name: v.name, locale, terms: true }),
    onSuccess: () => navigate({ to: '/login', search: { joined: 1 } }),
  });
  const register = useMutation({
    mutationFn: (v: Record<string, FormDataEntryValue>) => api.post<{ status: 'pending' | 'active' }>('api/v1/auth/register', {
      ...(kind === 'join' ? { join: { login: v.joinLogin, password: v.joinPassword } } : { kind, companyName: kind === 'iv' ? v.name : v.companyName, companyCode: v.companyCode || undefined }),
      name: v.name, locale, terms: v.terms === '1',
      ...(signup ? { signup: signup.ticket } : { email: v.email, password: v.password }),
    }),
    // Joined an active company: sign in (with Apple / Google again, or the password).
    onSuccess: (r) => navigate({ to: '/login', search: r.status === 'active' ? { joined: 1 } : { registered: 1 } }),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    const v = formValues(event);
    const same = Boolean(signup) || v.password === v.passwordConfirm;
    setMismatch(!same);
    if (same) register.mutate(v);
  };
  const offered = invites.data ?? [];
  if (signup && offered.length && !others) {
    return (
      <AuthCard eyebrow={t('Nauja paskyra')} title={t('Kvietimas')} footer={<a href="#others" onClick={(e) => { e.preventDefault(); setOthers(true); }}>{t('Ne, noriu sukurti naują įmonę arba prisijungti prie kitos')}</a>}>
        <Alert kind="success">{t('Registruojatės su {provider}: {email}', { provider: signup.provider, email: signup.email ?? '—' })}</Alert>
        {accept.isError ? <Alert kind="error">{errorText(t, accept.error)}</Alert> : null}
        <form onSubmit={(e) => { const v = formValues(e); accept.mutate({ invite: Number(v.invite), name: String(v.name) }); }}>
          {offered.map((i) => (
            <label key={i.id} className="remember-device">
              <input type="radio" name="invite" value={i.id} defaultChecked={i === offered[0]} required />
              <span><strong>{i.company}</strong> — {t('{name} kviečia jus prisijungti prie įmonės paskyros. Teisės: {role}.', { name: i.inviter ?? '—', role: t({ owner: 'Savininkas', member: 'Narys', viewer: 'Tik peržiūra' }[i.role]) })}</span>
            </label>
          ))}
          <label>{t('Vardas ir pavardė')} *<input name="name" autoComplete="name" required defaultValue={signup.name} /></label>
          <label className="remember-device"><input type="checkbox" name="terms" value="1" required /><span>{t('Sutinku, kad mano duomenys būtų tvarkomi paskyrai sukurti.')}</span></label>
          <button className="button button--primary" type="submit" disabled={accept.isPending}>{t('Priimti kvietimą')}</button>
        </form>
      </AuthCard>
    );
  }
  const choice = (key: typeof kind, label: string) => (
    <a href={`#${key}`} className={kind === key ? 'active' : undefined} aria-pressed={kind === key} onClick={(e) => { e.preventDefault(); setKind(key); }}>{t(label)}</a>
  );
  return (
    <AuthCard wide eyebrow={t('Nauja paskyra')} title={t('Registracija')}
      intro={kind === 'join' ? t('Įveskite įmonės prisijungimo vardą ir slaptažodį, kuriuos gavote iš įmonės savininko. Prisijungsite iš karto.') : t('Po registracijos paskyrą patvirtina administratorius. Tada galėsite prisijungti.')}
      footer={<>{t('Jau turite paskyrą?')} <Link to="/login">{t('Prisijungti')}</Link></>}>
      {signup ? <Alert kind="success">{t('Registruojatės su {provider}: {email}', { provider: signup.provider, email: signup.email ?? '—' })}</Alert> : null}
      {mismatch ? <Alert kind="error">{t('Slaptažodžiai nesutampa.')}</Alert> : null}
      {register.isError ? <Alert kind="error">{errorText(t, register.error)}</Alert> : null}
      {signup ? null : <SignInWith />}
      <form onSubmit={submit} autoComplete="on">
        <div className="calendar-view-switch register-kind" role="group" aria-label={t('Veiklos forma')}>
          {choice('company', 'Įmonė (MB, UAB…)')}{choice('iv', 'Individuali veikla')}{choice('join', 'Prisijungti prie įmonės')}
        </div>
        {kind === 'iv' ? <p className="muted">{t('Pvz., Bolt, Uber ar Wolt vairuotojams: pajamų ir išlaidų žurnalas, GPM, VSD ir PSD — {price} / mėn.', { price: '3,00 €' })}</p> : null}
        <div className="form-grid">
          {kind === 'company' ? <>
            <label>{t('Įmonės pavadinimas')} *<input name="companyName" autoComplete="organization" required autoFocus /></label>
            <label>{t('Įmonės kodas')}<input name="companyCode" /></label>
          </> : null}
          {kind === 'join' ? <>
            <label>{t('Įmonės prisijungimo vardas')} *<input name="joinLogin" autoComplete="off" autoCapitalize="none" required maxLength={40} autoFocus /></label>
            <label>{t('Įmonės slaptažodis')} *<input type="password" name="joinPassword" autoComplete="off" required maxLength={200} /></label>
          </> : null}
          <label>{t('Vardas ir pavardė')} *<input name="name" autoComplete="name" required defaultValue={signup?.name ?? ''} /></label>
          {signup ? null : <>
            <label>{t('El. paštas')} *<input type="email" name="email" autoComplete="email" required /></label>
            <label>{t('Slaptažodis')} *<input type="password" name="password" autoComplete="new-password" minLength={8} required /></label>
            <label>{t('Pakartokite slaptažodį')} *<input type="password" name="passwordConfirm" autoComplete="new-password" minLength={8} required /></label>
          </>}
        </div>
        <label className="remember-device"><input type="checkbox" name="terms" value="1" required /><span>{t('Sutinku, kad mano duomenys būtų tvarkomi paskyrai sukurti.')}</span></label>
        <button className="button button--primary" type="submit" disabled={register.isPending}>{t('Registruotis')}</button>
      </form>
    </AuthCard>
  );
}

/** First sign-in with a temporary password: only the new password and its repeat. */
export function NewPasswordPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mismatch, setMismatch] = useState(false);
  const save = useMutation({
    mutationFn: (password: string) => api.post('api/v1/auth/password', { newPassword: password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await navigate({ to: '/' });
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    const v = formValues(event);
    setMismatch(v.newPassword !== v.confirm);
    if (v.newPassword === v.confirm) save.mutate(String(v.newPassword));
  };
  return (
    <AuthCard eyebrow={t('Pirmas prisijungimas')} title={t('Sukurkite slaptažodį')} intro={t('Prisijungėte su laikinuoju slaptažodžiu. Susikurkite savo slaptažodį, kurį naudosite toliau.')}>
      {mismatch ? <Alert kind="error">{t('Slaptažodžiai nesutampa.')}</Alert> : null}
      {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
      <form onSubmit={submit}>
        <input type="text" name="username" value={me.user.email} autoComplete="username" hidden readOnly />
        <label>{t('Naujas slaptažodis')}<input type="password" name="newPassword" minLength={8} autoComplete="new-password" required autoFocus /></label>
        <label>{t('Pakartokite naują slaptažodį')}<input type="password" name="confirm" minLength={8} autoComplete="new-password" required /></label>
        <button className="button button--primary" type="submit" disabled={save.isPending}>{t('Išsaugoti ir tęsti')}</button>
      </form>
    </AuthCard>
  );
}

export function ForgotPage() {
  const { t } = useI18n();
  const send = useMutation({ mutationFn: (email: string) => api.post('api/v1/auth/forgot', { email }) });
  return (
    <AuthCard eyebrow={t('Klientų erdvė')} title={t('Pamiršote slaptažodį?')} footer={<Link to="/login">{t('Grįžti į prisijungimą')}</Link>}
      intro={send.isSuccess ? undefined : t('Įveskite paskyros el. pašto adresą. Atsiųsime nuorodą naujam slaptažodžiui nustatyti.')}>
      {send.isSuccess ? <Alert kind="success">{t('Jei šis el. pašto adresas užregistruotas, išsiuntėme nuorodą slaptažodžiui pakeisti. Patikrinkite paštą.')}</Alert> : (
        <>
          {send.isError ? <Alert kind="error">{errorText(t, send.error)}</Alert> : null}
          <form onSubmit={(e) => send.mutate(String(formValues(e).email))}>
            <label>{t('El. paštas')}<input type="email" name="email" autoComplete="username" required autoFocus /></label>
            <button className="button button--primary" type="submit" disabled={send.isPending}>{t('Siųsti nuorodą')}</button>
          </form>
        </>
      )}
    </AuthCard>
  );
}

export function ResetPage({ token }: { token: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mismatch, setMismatch] = useState(false);
  const reset = useMutation({
    mutationFn: (newPassword: string) => api.post('api/v1/auth/reset', { token, newPassword }),
    onSuccess: () => navigate({ to: '/login', search: { reset: 1 } }),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    const v = formValues(event);
    setMismatch(v.newPassword !== v.confirm);
    if (v.newPassword === v.confirm) reset.mutate(String(v.newPassword));
  };
  const invalid = reset.error instanceof ApiError && (reset.error.code === 'reset_link_invalid' || reset.error.code === 'invalid');
  return (
    <AuthCard eyebrow={t('Klientų erdvė')} title={t('Naujas slaptažodis')} footer={<Link to="/login">{t('Grįžti į prisijungimą')}</Link>}>
      {invalid ? <Alert kind="error">{t('Nuoroda nebegalioja arba jau panaudota. Paprašykite naujos.')} <Link to="/forgot">{t('Siųsti naują nuorodą')}</Link></Alert> : null}
      {mismatch ? <Alert kind="error">{t('Slaptažodžiai nesutampa.')}</Alert> : null}
      {reset.isError && !invalid ? <Alert kind="error">{errorText(t, reset.error)}</Alert> : null}
      <form onSubmit={submit}>
        <label>{t('Naujas slaptažodis')}<input type="password" name="newPassword" minLength={8} autoComplete="new-password" required autoFocus /></label>
        <label>{t('Pakartokite naują slaptažodį')}<input type="password" name="confirm" minLength={8} autoComplete="new-password" required /></label>
        <button className="button button--primary" type="submit" disabled={reset.isPending}>{t('Išsaugoti slaptažodį')}</button>
      </form>
    </AuthCard>
  );
}
